// Utils

import { householdMemberTools } from '../tools/householdMemberTools';
import { simpleCloneDeep, get, isEqual } from './objectHelpers';

// Constants

import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';

// Types

import {
    DirtyFlag,
    DirtyMedicaidVerification,
    HouseholdMember,
    OtherProgramTypeForDirtyFlag,
    OtherStateOrFederalProgram,
    SsapData
} from '@components/ssap/SsapTypes';

// Order to follow: ssacDataDirty -> esiMecDataDirty -> nonEsiMecDataDirty -> vlpDataDirty
// If flag need to be updated we pick 1 for verifications, otherwise 0
// For example: 1101 means: sacDataDirty: true; esiMecDataDirty: true; nonEsiMecDataDirty: false; vlpDataDirty: true

interface ConfigItem {
    path: string,
    medicaidVerifications: string,
    isArray?: boolean,
    searchPropName?: string,
    searchPropValue?: OtherProgramTypeForDirtyFlag
}

export const config: Array<ConfigItem> = [
    {
        path: 'name.firstName',
        medicaidVerifications: '1111'
    },
    {
        path: 'name.lastName',
        medicaidVerifications: '1111'
    },
    {
        path: 'dateOfBirth',
        medicaidVerifications: '1111'
    },
    {
        path: 'citizenshipImmigrationStatus.eligibleImmigrationDocumentType[0].I94Indicator',
        medicaidVerifications: '0001'
    },
    {
        path: 'citizenshipImmigrationStatus.eligibleImmigrationDocumentType[0].I20Indicator',
        medicaidVerifications: '0001'
    },
    {
        path: 'citizenshipImmigrationStatus.eligibleImmigrationDocumentType[0].I327Indicator',
        medicaidVerifications: '0001'
    },
    {
        path: 'citizenshipImmigrationStatus.eligibleImmigrationDocumentType[0].I551Indicator',
        medicaidVerifications: '0001'
    },
    {
        path: 'citizenshipImmigrationStatus.eligibleImmigrationDocumentType[0].I571Indicator',
        medicaidVerifications: '0001'
    },
    {
        path: 'citizenshipImmigrationStatus.eligibleImmigrationDocumentType[0].I766Indicator',
        medicaidVerifications: '0001'
    },
    {
        path: 'citizenshipImmigrationStatus.eligibleImmigrationDocumentType[0].I797Indicator',
        medicaidVerifications: '0001'
    },
    {
        path: 'citizenshipImmigrationStatus.eligibleImmigrationDocumentType[0].DS2019Indicator',
        medicaidVerifications: '0001'
    },
    {
        path: 'citizenshipImmigrationStatus.eligibleImmigrationDocumentType[0].I94InPassportIndicator',
        medicaidVerifications: '0001'
    },
    {
        path: 'citizenshipImmigrationStatus.eligibleImmigrationDocumentType[0].OtherDocumentTypeIndicator',
        medicaidVerifications: '0001'
    },
    {
        path: 'citizenshipImmigrationStatus.eligibleImmigrationDocumentType[0].TemporaryI551StampIndicator',
        medicaidVerifications: '0001'
    },
    {
        path: 'citizenshipImmigrationStatus.eligibleImmigrationDocumentType[0].MachineReadableVisaIndicator',
        medicaidVerifications: '0001'
    },
    {
        path: 'citizenshipImmigrationStatus.eligibleImmigrationDocumentType[0].UnexpiredForeignPassportIndicator',
        medicaidVerifications: '0001'
    },
    {
        path: 'socialSecurityCard.socialSecurityNumber',
        medicaidVerifications: '1110'
    },
    {
        path: 'gender',
        medicaidVerifications: '0110'
    },
    {
        path: 'householdContact.homeAddress.streetAddress1',
        medicaidVerifications: '0110'
    },
    {
        path: 'householdContact.homeAddress.streetAddress2',
        medicaidVerifications: '0110'
    },
    {
        path: 'householdContact.homeAddress.city',
        medicaidVerifications: '0110'
    },
    {
        path: 'householdContact.homeAddress.state',
        medicaidVerifications: '0110'
    },
    {
        path: 'householdContact.homeAddress.postalCode',
        medicaidVerifications: '0110'
    },
    {
        path: 'householdContact.homeAddress.countyCode',
        medicaidVerifications: '0110'
    },
    {
        path: 'householdContact.homeAddress.county',
        medicaidVerifications: '0110'
    },
    {
        path: 'householdContact.homeAddress.primaryAddressCountyFipsCode',
        medicaidVerifications: '0110'
    },
    {
        path: 'healthCoverage.employerWillOfferInsuranceIndicator',
        medicaidVerifications: '0100'
    },
    {
        path: 'healthCoverage.stateHealthBenefit',
        medicaidVerifications: '0100'
    },
    {
        path: 'incarcerationStatus.incarcerationStatusIndicator',
        medicaidVerifications: '1000'
    },
    {
        path: 'incarcerationStatus.incarcerationAsAttestedIndicator',
        medicaidVerifications: '1000'
    },
    {
        path: 'incarcerationStatus.incarcerationPendingDispositionIndicator',
        medicaidVerifications: '1000'
    },
    {
        path: 'healthCoverage.currentOtherInsurance.otherStateOrFederalPrograms',
        medicaidVerifications: '0010',
        isArray: true,
        searchPropName: 'type',
        searchPropValue: OtherProgramTypeForDirtyFlag.MEDICARE
    },
    {
        path: 'healthCoverage.currentOtherInsurance.otherStateOrFederalPrograms',
        medicaidVerifications: '0010',
        isArray: true,
        searchPropName: 'type',
        searchPropValue: OtherProgramTypeForDirtyFlag.NONE
    },
    {
        path: 'healthCoverage.currentOtherInsurance.otherStateOrFederalPrograms',
        medicaidVerifications: '0010',
        isArray: true,
        searchPropName: 'type',
        searchPropValue: OtherProgramTypeForDirtyFlag.OTHER_COVERAGE
    },
    {
        path: 'healthCoverage.currentOtherInsurance.otherStateOrFederalPrograms',
        medicaidVerifications: '0010',
        isArray: true,
        searchPropName: 'type',
        searchPropValue: OtherProgramTypeForDirtyFlag.PEACE_CORPS
    }
];

interface MaskForDirtyFlag {
    flag: string,
    value: number
}

const masks: Array<MaskForDirtyFlag> = [
    {
        flag: DirtyFlag.VLP_DATA_DIRTY,
        value: 1 << 0
    },
    {
        flag: DirtyFlag.NON_ESI_MEC_DATA_DIRTY,
        value: 1 << 1
    },
    {
        flag: DirtyFlag.ESI_MEC_DATA_DIRTY,
        value: 1 << 2
    },
    {
        flag: DirtyFlag.SSAC_DATA_DIRTY,
        value: 1 << 3
    }
];

const updateDirtyFlags = (data: SsapData, dataSnapshot: SsapData): SsapData => {

    type DirtyFlagsToUpdateForHouseholdMember = {
        applicantGuid: string,
        flags: {},
        initialFlags: DirtyMedicaidVerification
    }

    const dirtyFlagsToUpdate = data
        .taxHousehold
        .householdMember
        .reduce(
            (
                hhms: Array<DirtyFlagsToUpdateForHouseholdMember>,
                hhm: HouseholdMember
            ): Array<DirtyFlagsToUpdateForHouseholdMember> => {

                const { applicantGuid } = hhm;

                const hhmOnSessionStart = householdMemberTools
                    .extractFromHouseholdMembers(dataSnapshot.taxHousehold.householdMember)
                    .getHhmByGuid(applicantGuid);

                interface FoundDifferences {
                    paths: Array<string>,
                    verifications: Array<string>
                }

                const dataUpdates = config
                    .reduce(
                        (
                            rules: FoundDifferences,
                            rule: ConfigItem
                        ): FoundDifferences => {

                            const {
                                path,
                                medicaidVerifications,
                                isArray,
                                searchPropName,
                                searchPropValue
                            } = rule;

                            let currentValue, initialSessionValue;

                            if (isArray && searchPropName) {
                                currentValue = (get(hhm, path) || [])
                                    .find((el: OtherStateOrFederalProgram) => el[searchPropName] === searchPropValue) || {};

                                initialSessionValue = (get(hhmOnSessionStart, path) || [])
                                    .find((el: OtherStateOrFederalProgram) => el[searchPropName] === searchPropValue) || {};
                            } else {
                                currentValue = get(hhm, path);
                                initialSessionValue = get(hhmOnSessionStart, path);
                            }

                            const areEqual = isEqual(currentValue, initialSessionValue);

                            return !areEqual
                                ? {
                                    paths: [ ...rules.paths, path ],
                                    verifications: [ ...rules.verifications, '0b' + medicaidVerifications ]
                                }
                                : rules;
                        },
                        {
                            paths: [],
                            verifications: []
                        }
                    );

                const getDirtyFlags = () => {
                    const combinedVerifications = dataUpdates
                        .verifications
                        .reduce(
                            (combinedVerifications: number, verification: string): number => {
                                return combinedVerifications | +verification;
                            },
                            0
                        );

                    return masks
                        .reduce(
                            (flags: {}, mask: MaskForDirtyFlag): {} => {
                                const {
                                    flag,
                                    value
                                } = mask;

                                return {
                                    ...flags,
                                    [ flag ]: !!(combinedVerifications & value)
                                };
                            },
                            { }
                        );
                };

                return [
                    ...hhms,
                    {
                        applicantGuid,
                        flags: getDirtyFlags(),
                        initialFlags: get(hhmOnSessionStart, 'medicaidVerification')
                            || simpleCloneDeep(EMPTY_OBJECTS.MEDICAID_VERIFICATION)
                    }
                ];
            },
            [ ]
        );

    data.taxHousehold.householdMember = data
        .taxHousehold
        .householdMember
        .map((hhm: HouseholdMember) => {

            const { applicantGuid } = hhm;

            interface DefaultDirtyFlags {
                applicantGuid: null,
                flags: DirtyMedicaidVerification,
                initialFlags: 0
            }

            const defaultDirtyFlags: DefaultDirtyFlags = {
                applicantGuid: null,
                flags: simpleCloneDeep(EMPTY_OBJECTS.MEDICAID_VERIFICATION),
                initialFlags: 0
            };

            type CorrespondingDirtyFlagsToUpdate = DirtyFlagsToUpdateForHouseholdMember | undefined;

            const correspondingDirtyFlagsToUpdate: CorrespondingDirtyFlagsToUpdate = dirtyFlagsToUpdate
                .find((df: DirtyFlagsToUpdateForHouseholdMember): boolean => df.applicantGuid === applicantGuid);

            type DirtyFlags = CorrespondingDirtyFlagsToUpdate | DefaultDirtyFlags

            const dirtyFlags: DirtyFlags = correspondingDirtyFlagsToUpdate || defaultDirtyFlags;

            const dirtyFlagsToMerge = Object
                .keys(dirtyFlags.flags)
                .reduce((flags: {}, key: string): {} => {
                    return {
                        ...flags,
                        [ key ]: dirtyFlags.initialFlags[key] === true
                            ? true
                            : dirtyFlags.flags[key]
                    };
                }, { });

            return {
                ...hhm,
                medicaidVerification: {
                    ...hhm.medicaidVerification,
                    ...dirtyFlagsToMerge
                }
            };
        });

    return data;
};

export { updateDirtyFlags };